import type { log as it } from "../it/log.ts";
import { formati } from "../formati.ts";

const f = formati("en");

/**
 * Le righe che Portman scrive nei log di un'operazione. Si salvano con
 * l'operazione, quindi restano nella lingua dell'istanza di quel momento.
 */
export const log: typeof it = {
  // Attesa della salute
  "log.nonIspezionabile": "cannot be inspected",
  "log.reteRitenta": (p: { container: string; dettaglio: string }) =>
    `${p.container}: network connect failed (${p.dettaglio}); retrying…`,
  "log.avvioInCorso": "starting",
  "log.nonAncoraPresente": "not there yet",
  "log.pronto": (p: { container: string }) => `${p.container}: ready.`,
  "log.healthcheckDice": (p: { stato: string }) => `its healthcheck reports "${p.stato}"`,
  "log.containerE": (p: { stato: string }) => `the container is "${p.stato}"`,
  "log.portaChiusa": (p: { porta: number }) => `running, but port ${p.porta} refuses connections`,
  "log.portaIrraggiungibile": (p: { porta: number }) => `running, but port ${p.porta} isn't reachable from here`,
  "log.porteDichiarate": (p: { porte: readonly number[] }) =>
    ` — it declares ${f.plurale(p.porte.length, { one: "port", other: "ports" })} ${p.porte.join(", ")} instead`,
  "log.sondaMuta": (p: { container: string; porta: number }) =>
    `${p.container}: no healthcheck, and port ${p.porta} isn't reachable to check it. ` +
    "Settling for the process running: traffic moves as soon as it starts.",
  "log.portaAperta": (p: { container: string; porta: number }) =>
    `${p.container}: no healthcheck; port ${p.porta} accepts connections.`,
  "log.attesa": (p: { container: string; motivo: string; secondi: number }) =>
    `${p.container}: ${p.motivo}; waited ${p.secondi} s so far.`,

  // Rilascio blue/green
  "log.preparazione": (p: { generazione: string }) =>
    `Preparing generation "${p.generazione}" alongside the running one…`,
  "log.avvioFissi": (p: { progetto: string }) => `Starting pinned services in project "${p.progetto}"…`,
  "log.ricollegaFissi": "Reconnecting pinned services to the running generation…",
  "log.creaGenerazione": "Creating the new generation (not started)…",
  "log.collegaFissi": "Connecting pinned services to the new generation…",
  "log.avvioGenerazione": "Starting the new generation…",
  "log.attesaGenerazione": "Waiting for the new generation to be ready…",
  "log.avvioFallito": "Start failed: removing the incomplete generation.",
  "log.spostamento": "Moving traffic to the new generation…",
  "log.rimozioneExFissi": (p: { servizi: string; progetto: string }) =>
    `Removing ${p.servizi} from project "${p.progetto}": no longer pinned…`,
  "log.drenaggio": (p: { secondi: number }) => `Waiting ${p.secondi}s for in-flight requests to finish…`,
  "log.completato": "Deploy complete with no downtime.",
  "log.rimozionePrecedente": (p: { progetto: string }) =>
    `Removing the previous version (project "${p.progetto}")…`,

  // Operazioni
  "log.annullata": (p: { motivo: string }) => `Operation canceled: ${p.motivo}`,
  "log.fallita": (p: { motivo: string }) => `Operation failed: ${p.motivo}`,
  "log.avvioServizi": "Starting services…",
  "log.avviato": "Stack started.",
  "log.arrestoServizi": "Stopping services…",
  "log.riavvioServizi": "Restarting services…",
  "log.scaricamento": "Pulling images…",
  "log.nessunDettaglio": "no details",
  "log.creaRete": (p: { rete: string }) => `Creating network "${p.rete}", declared in the compose…`,
  "log.operazioneInCorso": (p: { stack: string }) =>
    `an operation is already running on "${p.stack}": wait for it to finish.`,
  "log.verifica": "Checking the configuration…",
  "log.rimozioneGenerazione": (p: { generazione: string }) =>
    `Removing generation "${p.generazione}", replaced by the recreate…`,
  "log.arrestato": "Stack stopped. The configuration stays saved.",
  "log.arresto": (p: { stack: string }) => `Stopping "${p.stack}"…`,
  "log.copiaVolume": (p: { da: string; a: string }) => `Copying volume ${p.da} to ${p.a}…`,
  "log.vecchioFermo": (p: { stack: string }) =>
    `"${p.stack}" is stopped and keeps its name: restart it from here if needed.`,
  "log.rinominato": (p: { da: string; a: string }) => `Stack renamed from "${p.da}" to "${p.a}".`,
  "log.volumiPrecedenti": (p: { volumi: string }) =>
    `The previous volumes stay on the host: ${p.volumi}. Delete them once you've checked the data.`,
  "log.rimozioneApplicazioni": (p: { progetto: string }) =>
    `Removing the apps from project "${p.progetto}", pinned services stay…`,
  "log.portaOccupata": (p: { porta: number; da: string; libera: number | null }) =>
    `port ${p.porta} is taken by ${p.da}` + (p.libera === null ? "" : ` (free: ${p.libera})`),
  "log.nomeOccupato": (p: { nome: string; servizio: string; stato: string; progetto: string | null }) =>
    `"${p.nome}" (service ${p.servizio}) is already used by a ${p.stato} container ` +
    (p.progetto === null ? "started outside Compose" : `from Compose project "${p.progetto}"`),

  "log.processoHost": "a host process outside Docker",
  "log.tagMancante": "missing tag",
  "log.tagNonValido": "invalid tag",
  "log.utenteSconosciuto": "unknown user",
  "log.passwordErrata": "wrong password",
  "log.utenteDisattivato": "user deactivated",

  // Log del server, per chi lo gestisce
  "server.emailNonInviata": "alert email not sent",
  "server.valutazioneAvvisi": "alert evaluation failed",
  "server.avvisoEvento": "event alert not handled",
  "server.statisticheAssenti": "container stats unavailable",
  "server.dopoGiro": "post-round processing failed",
  "server.giroFallito": "sampling round failed",
  "server.dopoEvento": "post-event processing failed",
  "server.eventoNonGestito": "container event not handled",
  "server.streamCaduto": "event stream dropped",
  "server.riassunto": "metrics rollup",
  "server.potatura": "metrics pruning",

  "server.ripristinato":
    "Database restored. Portman is restarting: reload the page in a few seconds " +
    "and sign in with the credentials from the backup.",
  "server.conservaToken": "Keep this token: it won't be shown again.",
  "server.chiaveLunghezza": (p: { attesi: number; ricevuti: number }) =>
    `the key must be ${p.attesi} bytes, got ${p.ricevuti}`,
  "server.cifratoFormato": "malformed ciphertext: expected <version>.<nonce>.<data>",
  "server.cifraturaVersione": (p: { versione: string }) => `unsupported encryption version: ${p.versione}`,
  "server.decifrare": "cannot decrypt: wrong key or tampered data",
  "server.cifratoNonce": "malformed ciphertext: wrong nonce length",

  // Messaggi delle revisioni create da Portman
  "revisione.descrizioneImport": (p: { progetto: string }) => `imported from Compose project "${p.progetto}"`,
  "revisione.descrizioneProxy": "Reverse proxy for zero-downtime deploys, installed by Portman.",
  "revisione.creazione": "stack created",
  "revisione.copia": (p: { stack: string; revisione: number | null }) => `copy of ${p.stack} (rev ${p.revisione})`,
  "revisione.ritorno": (p: { numero: number }) => `back to revision ${p.numero}`,
  "revisione.importazione": (p: { progetto: string }) =>
    `imported from existing containers (project "${p.progetto}")`,
  "revisione.porteRimosse": "removed host ports for zero-downtime deploys",
  "revisione.proxy": "Nginx Proxy Manager installed",
  "revisione.tag": (p: { tag: string }) => `tag updated to ${p.tag}`,
  "revisione.webhook": (p: { tag: string }) => `automatic deploy ${p.tag}`,
  "log.ripristino": (p: { numero: number }) => `Restoring revision ${p.numero}…`,
  "log.ritentaRete": (p: { dettaglio: string; tentativo: number; totale: number; secondi: number }) =>
    `The network or registry hiccuped (${p.dettaglio}). Attempt ${p.tentativo} of ${p.totale} in ${p.secondi} s…`,
  "log.pullFallito": (p: { dettaglio: string }) =>
    `Pull failed (${p.dettaglio}). Continuing with the images already on the host: ` +
    "if one is missing, the start will stop right after with an explicit error.",
};
